import {Button, ButtonProps, Tag} from 'platform/components';
import {HStack, Show, ValueByDevice, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {RecordAuditResponseBody, RecordAuditState} from '@dms/api/core';
import i18n from '@dms/i18n';

import {buildArray, suffixTestId} from 'shared/web';

import {RecordAuditFillPanel} from './RecordAuditFillPanel';
import {RecordAuditParticipation} from './RecordAuditParticipation';
import {SignRecordAuditModal} from './SignRecordAuditModal';
import {useOptimisticAuditHeader} from './useOptimisticAuditHeader';
import {RECORD_AUDIT_STATE_COLOR_SCHEME, useRecordAuditLifecycle} from './useRecordAuditLifecycle';
import {useRecordAuditTeamPickers} from './useRecordAuditTeamPickers';

// The embed lives beside the service case navigation, so its container is ~350px narrower than
// the detail page at the same viewport — fewer columns keep every member card above its
// min-content width (no clipped role chips, no action icons escaping the card).
const EMBED_PARTICIPATION_COLUMNS: ValueByDevice<number> = [1, 1, 2, 3];

interface RecordAuditFillManagerProps {
  audit: RecordAuditResponseBody;
  isReadOnly?: boolean;
  /**
   * Chrome of the participation panel and fill panel. `flat` is for hosts that already wrap
   * the embed in a card (e.g. the service order tab) — no nested cards. Defaults to `card`.
   */
  variant?: 'card' | 'flat';
  'data-testid'?: string;
}

/**
 * Full record-audit management surface for embedding outside the audit detail page (e.g. the
 * service order Audits tab): the same participation panel and optimistic-header logic the detail
 * page uses (auditor / responsible person / participants — required before close), the lifecycle
 * actions (close / sign / reopen / export), the signing modal, and the reusable OK/BAD/NA fill
 * panel. Deliberately omits the branch and record-pairing controls, which stay on the detail page.
 */
export function RecordAuditFillManager(props: RecordAuditFillManagerProps) {
  const {audit} = props;

  // Same optimistic header state machine the detail page drives (auditor/responsible/participants).
  const header = useOptimisticAuditHeader(audit);
  const lifecycle = useRecordAuditLifecycle(audit);
  const pickers = useRecordAuditTeamPickers(header);

  const isEditable = !props.isReadOnly && audit.state === 'OPEN';

  // Close needs an auditor + responsible person (the BE also requires every task answered).
  const canAttemptClose =
    isNotNil(header.effectiveAuditorId) && isNotNil(header.effectiveResponsiblePersonId);

  const actions = match<RecordAuditState, ButtonProps[]>(audit.state)
    .with('OPEN', () =>
      buildArray<ButtonProps>().whenNot(props.isReadOnly, {
        variant: 'primary',
        title: i18n.t('general.actions.close'),
        onClick: lifecycle.handleClose,
        isDisabled: !canAttemptClose,
        isLoading: lifecycle.isClosing,
        'data-testid': suffixTestId('close', props),
      })
    )
    .with('CLOSED', () =>
      buildArray<ButtonProps>()
        .whenNot(props.isReadOnly, {
          variant: 'primary',
          title: i18n.t('page.recordAudit.actions.sign'),
          onClick: lifecycle.openSignModal,
          'data-testid': suffixTestId('sign', props),
        })
        .whenNot(props.isReadOnly, {
          variant: 'secondary',
          title: i18n.t('page.recordAudit.actions.reopen'),
          onClick: lifecycle.handleReopen,
          isLoading: lifecycle.isReopening,
          'data-testid': suffixTestId('reopen', props),
        })
    )
    .with('SIGNED', () =>
      buildArray<ButtonProps>().add({
        variant: 'primary',
        title: i18n.t('page.recordAudit.actions.exportPdf'),
        onClick: lifecycle.handleExportPdf,
        isLoading: lifecycle.isExportingPdf,
        'data-testid': suffixTestId('exportPdf', props),
      })
    )
    .exhaustive();

  return (
    <VStack spacing={4}>
      <HStack justify="space-between" align="center" spacing={3} wrap>
        <Tag
          colorScheme={RECORD_AUDIT_STATE_COLOR_SCHEME[audit.state]}
          label={i18n.t(`entity.recordAudit.states.${audit.state}`)}
        />
        <Show when={actions.length > 0}>
          <HStack spacing={2}>
            {actions.map((action) => (
              <Button key={action.title} {...action} />
            ))}
          </HStack>
        </Show>
      </HStack>

      {/* Reused detail-page participation panel: auditor + responsible person (required to close) +
          participants. */}
      <RecordAuditParticipation
        data-testid={suffixTestId('participation', props)}
        variant={props.variant}
        columns={EMBED_PARTICIPATION_COLUMNS}
        auditorEmployeeId={header.effectiveAuditorId}
        responsiblePersonEmployeeId={header.effectiveResponsiblePersonId}
        participants={header.realParticipants}
        nameByEmployeeId={header.nameByEmployeeId}
        isEditable={isEditable}
        isAuditorUpdating={header.isAuditorUpdating}
        isResponsiblePersonUpdating={header.isResponsiblePersonUpdating}
        loadingParticipantCount={header.loadingParticipantCount}
        onAddParticipant={pickers.openAddParticipantDialog}
        onRemoveParticipant={header.removeParticipant}
        onEditAuditor={pickers.openAuditorPicker}
        onEditResponsiblePerson={pickers.openResponsiblePersonPicker}
      />

      <RecordAuditFillPanel audit={audit} isEditable={isEditable} variant={props.variant} />

      <SignRecordAuditModal
        auditId={audit.id}
        auditorName={header.auditorName}
        responsiblePersonName={header.responsiblePersonName}
        isOpen={lifecycle.isSignModalOpen}
        onClose={lifecycle.closeSignModal}
      />
    </VStack>
  );
}
